
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SearchFilters {
  discipline?: string;
  status?: string;
  nodeType?: string;
  tags?: string[];
}

interface SearchResult {
  id: string;
  title: string;
  content: any;
  node_type: string;
  status: string;
  discipline_id: string;
  author_id: string;
  created_at: string;
  updated_at: string;
  published_at: string;
  discipline_name: string;
  discipline_slug: string;
  author_username: string;
  author_full_name: string;
  tags: string[];
  search_rank: number;
}

interface SearchSuggestion {
  suggestion: string;
  type: string;
  count: number;
}

export const useSearch = (
  searchQuery: string,
  filters: SearchFilters = {},
  limit: number = 20,
  offset: number = 0
) => {
  return useQuery({
    queryKey: ['search', searchQuery, filters, limit, offset],
    queryFn: async (): Promise<SearchResult[]> => {
      const { data, error } = await supabase.rpc('search_nodes', {
        search_query: searchQuery,
        discipline_filter: filters.discipline || null,
        status_filter: filters.status || null,
        node_type_filter: filters.nodeType || null,
        tag_filter: filters.tags || null,
        limit_count: limit,
        offset_count: offset,
      });

      if (error) throw error;
      return data || [];
    },
    enabled: true, // Always enabled, will return recent content when query is empty
  });
};

export const useSearchSuggestions = (searchQuery: string, limit: number = 10) => {
  return useQuery({
    queryKey: ['search-suggestions', searchQuery, limit],
    queryFn: async (): Promise<SearchSuggestion[]> => {
      if (!searchQuery.trim()) return [];

      const { data, error } = await supabase.rpc('get_search_suggestions', {
        search_query: searchQuery,
        limit_count: limit,
      });

      if (error) throw error;
      return data || [];
    },
    enabled: searchQuery.trim().length > 0,
  });
};
